import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Car, Wrench, Battery, Fuel, Key, Phone, Shield, Clock, Star,
  CheckCircle, ChevronRight, Zap, Users, TrendingUp, Award,
  MapPin, CreditCard, Truck, Crown, ArrowRight, Ban, Handshake
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customer');

  const customerFeatures = [
    { icon: Clock, title: '24/7 Availability', desc: 'Get help any time of day or night, wherever you are.' },
    { icon: MapPin, title: 'Real-Time Tracking', desc: 'Watch your driver approach on live tracking.' },
    { icon: Shield, title: 'Reliable Drivers', desc: 'Every driver is verified and rated by real customers.' },
    { icon: CreditCard, title: 'Transparent Pricing', desc: 'See the full quote before you commit — no surprises.' },
    { icon: Car, title: 'My Garage', desc: 'Save your vehicles for faster requests every time.' },
    { icon: Star, title: 'Rate & Review', desc: 'Help keep quality high by rating your experience.' },
  ];

  const whyWheely = [
    { icon: Zap, title: 'Dead simple', desc: 'Tell us where you are and what\'s wrong. Get real quotes from real local drivers in minutes — no forms, no hold music, no waiting on the phone.' },
    { icon: Ban, title: 'Zero fees for you', desc: 'No commission, no subscription, no booking fee. The price you\'re quoted for the job is the price you pay — that\'s it.' },
    { icon: Handshake, title: 'Drivers cover the cost', desc: 'We charge a small commission to the driver on each job, never the customer. That\'s the whole business model — nothing hidden.' },
  ];

  const services = [
    { icon: Battery, label: 'Battery Jump Start' },
    { icon: Fuel, label: 'Fuel Delivery' },
    { icon: Key, label: 'Lockout Assistance' },
    { icon: Wrench, label: 'Tyre Change' },
    { icon: Car, label: 'Vehicle Recovery' },
    { icon: Zap, label: 'Emergency Repair' },
  ];

  const tiers = [
    {
      name: 'Bronze',
      badge: '3 MONTHS FREE',
      badgeColor: 'bg-amber-700',
      price: '£0',
      period: 'for 90 days',
      then: 'then £29.99/mo',
      commission: '5%',
      description: 'Perfect for new & solo operators proving the platform.',
      color: 'border-amber-700/50',
      headerBg: 'from-amber-900/40 to-amber-800/20',
      textColor: 'text-amber-500',
      features: [
        '3 months completely free',
        'Access to all service requests',
        '5% commission per completed job',
        'Standard customer support',
        'Performance dashboard',
        'Upgrade any time with discount',
      ],
      popular: false,
    },
    {
      name: 'Silver',
      badge: 'POPULAR',
      badgeColor: 'bg-slate-500',
      price: '£49.99',
      period: 'per month',
      then: null,
      commission: '3.5%',
      description: 'For growing operators who want better volume and margins.',
      color: 'border-slate-400/50',
      headerBg: 'from-slate-700/40 to-slate-600/20',
      textColor: 'text-slate-300',
      features: [
        'Better lead quality & volume',
        '3.5% commission per job',
        'Priority customer support',
        'Performance analytics',
        'Higher job frequency',
        'Upgrade discount available',
      ],
      popular: true,
    },
    {
      name: 'Gold',
      badge: 'MAXIMUM EARNINGS',
      badgeColor: 'bg-yellow-600',
      price: '£99.99',
      period: 'per month',
      then: null,
      commission: '2.5%',
      description: 'For professional fleets who want maximum profit and priority.',
      color: 'border-yellow-500/50',
      headerBg: 'from-yellow-900/40 to-yellow-800/20',
      textColor: 'text-yellow-400',
      features: [
        'Maximum earnings (2.5% commission)',
        'Priority leads — first access to requests',
        'Premium 24/7 support',
        'Advanced analytics & fleet tools',
        'Highest job volume',
        'First access to new features',
      ],
      popular: false,
    },
  ];

  const steps = [
    { num: '01', title: 'Request Help', desc: 'Tell us your location and what you need — takes 60 seconds.' },
    { num: '02', title: 'Get Quotes', desc: 'Nearby drivers send you competitive quotes instantly.' },
    { num: '03', title: 'Pay & Track', desc: 'Accept a quote and pay securely to book your driver, then track them live to your location.' },
  ];

  return (
    <div className="min-h-screen bg-wheely-navy-950 text-white">

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-wheely-navy-950/90 backdrop-blur border-b border-wheely-navy-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Wheely" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-lg">Wheely</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#drivers" className="hover:text-white transition-colors">For Drivers</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button className="bg-wheely-orange hover:bg-wheely-orange-dark text-white" onClick={() => navigate('/login')}>
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-wheely-orange/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <Badge className="bg-wheely-orange/20 text-wheely-orange-light border-wheely-orange/30 mb-6 px-4 py-1 text-sm">
            🚗 Emergency Roadside Assistance — UK Wide
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Stuck on the road?
            <span className="text-wheely-orange block">We've got you.</span>
          </h1>
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            Wheely connects you with verified local breakdown specialists in minutes.
            Get competitive quotes, track your driver live, and pay securely — all in one app.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-wheely-orange hover:bg-wheely-orange-dark text-white text-lg px-8 py-6 rounded-xl"
              onClick={() => navigate('/login')}
            >
              Get Help Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-wheely-navy-700 text-zinc-300 hover:bg-wheely-navy-800 text-lg px-8 py-6 rounded-xl"
              onClick={() => { setActiveTab('driver'); document.getElementById('drivers')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              <Truck className="mr-2 w-5 h-5" /> Join as a Driver
            </Button>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-zinc-500">
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> No subscription for customers</div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Pay only when helped</div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Reliable & rated drivers</div>
          </div>
        </div>
      </section>

      {/* Why Wheely */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <Badge className="bg-wheely-orange/20 text-wheely-orange-light border-wheely-orange/30 px-4 py-1">Why Wheely</Badge>
          </div>
          <h2 className="text-3xl font-bold text-center mb-3">Roadside help, without the runaround</h2>
          <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
            We're not a call centre with hidden charges. Wheely is a simple, direct connection between you and a local breakdown driver — nothing more.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {whyWheely.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-wheely-navy-800/40 border border-wheely-navy-700/40 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-wheely-orange/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-wheely-orange-light" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 px-4 bg-wheely-navy-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3">Services we cover</h2>
          <p className="text-zinc-400 text-center mb-10">From a flat battery to a full recovery — local specialists for every situation.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {services.map(({ icon: Icon, label }) => (
              <div key={label} className="bg-wheely-navy-800/60 border border-wheely-navy-700/50 rounded-xl p-5 flex items-center gap-4 hover:border-wheely-orange/40 transition-colors">
                <div className="w-10 h-10 bg-wheely-orange/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-wheely-orange-light" />
                </div>
                <span className="font-medium text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3">How it works</h2>
          <p className="text-zinc-400 text-center mb-12">Help in three simple steps.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={step.num} className="relative">
                <div className="text-6xl font-black text-wheely-orange/20 mb-3">{step.num}</div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <ChevronRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-zinc-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Features */}
      <section className="py-16 px-4 bg-wheely-navy-900/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3">Everything you need</h2>
          <p className="text-zinc-400 text-center mb-12">Built to make a stressful situation as easy as possible.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {customerFeatures.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-wheely-navy-800/40 border border-wheely-navy-700/40 rounded-xl p-6 hover:border-wheely-orange/30 transition-colors">
                <div className="w-10 h-10 bg-wheely-orange/20 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-wheely-orange-light" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              size="lg"
              className="bg-wheely-orange hover:bg-wheely-orange-dark text-white px-8"
              onClick={() => navigate('/login')}
            >
              Sign Up Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Driver Section */}
      <section id="drivers" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <Badge className="bg-wheely-orange/20 text-wheely-orange-light border-wheely-orange/30 px-4 py-1">For Breakdown Specialists</Badge>
          </div>
          <h2 className="text-3xl font-bold text-center mb-3">Earn more with Wheely</h2>
          <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
            Join a growing network of breakdown specialists. Set your own hours, pick the jobs you want, and keep more of what you earn.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: TrendingUp, title: 'Low Commission', desc: 'Keep 95–97.5% of every job. No hidden fees.' },
              { icon: Users, title: 'Your Own Hours', desc: 'Go online and offline whenever suits you.' },
              { icon: Award, title: 'Free to Start', desc: '3-month free Bronze trial — no upfront cost.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-wheely-navy-800/40 border border-wheely-navy-700/40 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-wheely-orange/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-wheely-orange-light" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-zinc-400 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-wheely-navy-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <Badge className="bg-wheely-orange/20 text-wheely-orange-light border-wheely-orange/30 px-4 py-1">Driver Membership Plans</Badge>
          </div>
          <h2 className="text-3xl font-bold text-center mb-3">Simple, transparent pricing</h2>
          <p className="text-zinc-400 text-center mb-12">Start free for 3 months. Upgrade when you're ready.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative bg-wheely-navy-900 border-2 ${tier.color} rounded-2xl overflow-hidden flex flex-col ${tier.popular ? 'ring-2 ring-slate-400/50 scale-105' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-slate-600 text-center text-xs font-bold py-1 text-white">
                    MOST POPULAR
                  </div>
                )}
                <div className={`bg-gradient-to-br ${tier.headerBg} p-6 ${tier.popular ? 'pt-8' : ''}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className={`text-2xl font-black ${tier.textColor}`}>{tier.name}</div>
                      <div className="text-zinc-400 text-sm mt-1">{tier.description}</div>
                    </div>
                    <span className={`${tier.badgeColor} text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap`}>
                      {tier.badge}
                    </span>
                  </div>
                  <div className="mt-4">
                    <span className={`text-4xl font-black ${tier.textColor}`}>{tier.price}</span>
                    <span className="text-zinc-400 text-sm ml-1">/{tier.period}</span>
                    {tier.then && <div className="text-zinc-500 text-xs mt-1">{tier.then}</div>}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 bg-wheely-navy-800/60 rounded-full px-3 py-1">
                    <span className="text-xs text-zinc-400">Commission:</span>
                    <span className={`text-sm font-bold ${tier.textColor}`}>{tier.commission} per job</span>
                  </div>
                </div>
                <div className="p-6 flex-1">
                  <ul className="space-y-3">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <CheckCircle className={`w-4 h-4 ${tier.textColor} flex-shrink-0 mt-0.5`} />
                        <span className="text-zinc-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 pt-0">
                  <Button
                    className={`w-full ${tier.popular ? 'bg-slate-600 hover:bg-slate-500' : 'bg-wheely-navy-800 hover:bg-zinc-700'} text-white`}
                    onClick={() => navigate('/login')}
                  >
                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-zinc-500 text-sm mt-8">
            All plans include access to the full platform. Upgrade or downgrade any time.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-wheely-orange/20 to-wheely-orange-dark/10 border border-wheely-orange/20 rounded-3xl p-12">
            <h2 className="text-4xl font-bold mb-4">Ready to get started?</h2>
            <p className="text-zinc-400 mb-8 text-lg">
              Join Wheely today — whether you need roadside help or want to earn as a specialist.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-wheely-orange hover:bg-wheely-orange-dark text-white text-lg px-8 py-6 rounded-xl"
                onClick={() => navigate('/login')}
              >
                I need help <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-wheely-navy-700 text-zinc-300 hover:bg-wheely-navy-800 text-lg px-8 py-6 rounded-xl"
                onClick={() => navigate('/login')}
              >
                <Truck className="mr-2 w-5 h-5" /> I'm a specialist
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-wheely-navy-700 py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Wheely" className="w-6 h-6 rounded object-cover" />
            <span className="font-semibold text-zinc-300">Wheely</span>
            <span>© 2026</span>
          </div>
          <p className="text-xs text-zinc-500 text-center md:text-left">
            Wheely Rescue Ltd, registered in Wales, company no. 17477317. Registered office: International House, 10 Churchill Way, Cardiff CF10 2HE.{' '}
            <a href="mailto:hello@wheelyrescue.co.uk" className="hover:text-white transition-colors">hello@wheelyrescue.co.uk</a>
          </p>
          <div className="flex gap-6">
            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Sign In</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
